import client, { mock, isRealApiEnabled } from "./client";
import seed from "./mocks/promotions";

/**
 * Promotions admin API (mock-backed).
 *
 * Mirrors the products namespace seam so the Promotions page can later switch
 * from mock data to `client.get(...)` without consumers changing. The promotion
 * shape and the editor are still being defined (see meeting notes), so this is
 * intentionally read-only for now: list / get / listTypes.
 */

const PROMOTIONS = seed.map((p) => ({ ...p }));

// Human-facing status filter labels map to the canonical status values stored
// on each promotion.
const STATUS_FILTER_MAP = {
  Published: "published",
  Scheduled: "scheduled",
  "In Review": "in_review",
  Archived: "archived",
};

const matchesSearch = (promo, search) => {
  if (!search) return true;
  const q = search.toLowerCase();
  return (
    (promo.name || "").toLowerCase().includes(q) ||
    (promo.type || "").toLowerCase().includes(q)
  );
};

const sortPromotions = (items, sort) => {
  const sorted = items.slice();
  switch (sort) {
    case "name_asc":
      return sorted.sort((a, b) => (a.name || "").localeCompare(b.name || ""));
    case "name_desc":
      return sorted.sort((a, b) => (b.name || "").localeCompare(a.name || ""));
    case "status_asc":
      return sorted.sort((a, b) =>
        (a.status || "").localeCompare(b.status || "")
      );
    case "status_desc":
      return sorted.sort((a, b) =>
        (b.status || "").localeCompare(a.status || "")
      );
    case "updated_asc":
      return sorted.sort(
        (a, b) => new Date(a.updatedAt) - new Date(b.updatedAt)
      );
    case "updated_desc":
    default:
      return sorted.sort(
        (a, b) => new Date(b.updatedAt) - new Date(a.updatedAt)
      );
  }
};

/**
 * @endpoint GET /api/v1/promotions
 * @param {{ search?: string, filters?: { status?: string[], type?: string[] },
 *           sort?: string, page?: number, pageSize?: number }} [params]
 * @returns {Promise<{ items: object[], total: number, page: number }>}
 */
export function list(params = {}) {
  const {
    search = "",
    filters = {},
    sort = "updated_desc",
    page = 1,
    pageSize = 8,
  } = params;
  if (isRealApiEnabled()) {
    // Mirror the products namespace: serialize the nested filters object into a
    // single JSON query param so status/type survive the trip. Sending the raw
    // nested object would JSON-encode it under a `filters` key that the BE never
    // reads, silently dropping the filters.
    return client.get("/v1/promotions", {
      query: {
        search,
        sort,
        page,
        pageSize,
        filters: JSON.stringify(filters || {}),
      },
    });
  }

  let items = PROMOTIONS.filter((p) => matchesSearch(p, search));

  const statuses = filters.status || [];
  if (statuses.length) {
    const wanted = statuses.map((s) => STATUS_FILTER_MAP[s] || s.toLowerCase());
    items = items.filter((p) => wanted.includes(p.status));
  }

  const types = filters.type || [];
  if (types.length) {
    items = items.filter((p) => types.includes(p.type));
  }

  items = sortPromotions(items, sort);

  const total = items.length;
  const start = (page - 1) * pageSize;
  return mock({ items: items.slice(start, start + pageSize), total, page });
}

/**
 * @endpoint GET /api/v1/promotions/:id
 */
export function get(id) {
  if (isRealApiEnabled())
    return client.get(`/v1/promotions/${encodeURIComponent(id)}`);
  return mock(PROMOTIONS.find((p) => p.id === id) || null);
}

/**
 * @endpoint GET /api/v1/promotions/types
 * Distinct promotion types for the "Filter by Type" group.
 */
export function listTypes() {
  if (isRealApiEnabled()) return client.get("/v1/promotions/types");
  return mock(Array.from(new Set(PROMOTIONS.map((p) => p.type))));
}
